import { handleInfluenceState } from './teams';
import { handleCommercialState, handleCommercialFailure } from './commercial';
/**
 * Domain Message Router
 * Routes incoming match data messages to appropriate domain handlers
 * Auto-registers with NakamaService on module load (dependency injection pattern)
 */

import { nakamaService } from '../services/nakama/nakama-service';
import { OpCode } from '../constants/op-codes';
import { abandonUnreadableResponse, acknowledgeGatedResponse } from './command-gate';
import { handlePlayerBanned } from './ban-notification';
import { handleSessionEnded } from './session-ended';
import {
  isPlayerJoinedMessage,
  isPlayersListMessage,
  isPlayerLeftMessage,
  isLiveKitTokenMessage,
  isPrivateChatStartMessage,
  isPrivateChatEndMessage,
  isSessionEndedMessage,
  isPlayerBannedMessage,
  isAirportsStateMessage,
  isFleetsStateMessage,
  isAvailablePlanesStateMessage,
  isAirplaneModelsStateMessage,
  isAddFleetSlotSuccessMessage,
  isAddFleetSlotFailedMessage,
  isRemoveFleetSlotSuccessMessage,
  isRemoveFleetSlotFailedMessage,
  isUpdateFleetSlotSuccessMessage,
  isUpdateFleetSlotFailedMessage,
  isPurchaseAirplanesSuccessMessage,
  isPurchaseAirplanesFailedMessage,
  isSellAirplanesSuccessMessage,
  isSellAirplanesFailedMessage,
  isQueryMaxFlightsResultMessage,
  isSegmentsStateMessage,
  isAddSegmentSuccessMessage,
  isAddSegmentFailedMessage,
  isUpdateFlightFrequencySuccessMessage,
  isUpdateFlightFrequencyFailedMessage,
  isRemoveFlightSuccessMessage,
  isRemoveFlightFailedMessage,
  isTeamSegmentsStateMessage,
  isTeamSegmentsUpdateMessage,
  isUpdateItineraryPriceSuccessMessage,
  isUpdateItineraryPriceFailedMessage,
  isUpdateRoutePricingPolicySuccessMessage,
  isUpdateRoutePricingPolicyFailedMessage,
  isResetItineraryPriceToAutoSuccessMessage,
  isResetItineraryPriceToAutoFailedMessage,
  isAirportFlightsStateMessage,
  isAirportFlightUpdateMessage,
  isAirportFlightRemovedMessage,
  isFinancialReportMessage,
  isQuarterlyReportMessage,
  isAnnualReportMessage,
  isCFOStatementsMessage,
  isTTMMetricsMessage,
  isReportsHistoryMessage,
  type ReportsHistoryMessage,
  isStrengthReportMessage,
  isTimerSyncMessage,
  isTickProcessingStateMessage,
  isCapitalDebtQuoteSuccessMessage,
  isCapitalDebtQuoteFailedMessage,
  isCapitalDebtOrderSuccessMessage,
  isCapitalDebtOrderFailedMessage,
  isCapitalEquityQuoteSuccessMessage,
  isCapitalEquityQuoteFailedMessage,
  isCapitalEquityOrderSuccessMessage,
  isCapitalEquityOrderFailedMessage,
  isCapitalStateMessage,
  isDevSaveStressLocalSuccessMessage,
  isDevSaveStressLocalFailedMessage,
  isAdminAddResourcesSuccessMessage,
  isAdminAddResourcesFailedMessage,
  isAdminJumpToNextTickSuccessMessage,
  isAdminJumpToNextTickFailedMessage,
  isAdminSetInnovationSpendLockedSuccessMessage,
  isAdminSetInnovationSpendLockedFailedMessage,
  isAdminForceEndMatchSuccessMessage,
  isAdminForceEndMatchFailedMessage,
  isUnlockInnovationSuccessMessage,
  isUnlockInnovationFailedMessage,
  isMissionsStateMessage,
  isMissionProgressMessage,
  isMissionCompletedMessage,
  isPolicyStatesSyncMessage,
  isTogglePolicySuccessMessage,
  isTogglePolicyFailedMessage,
  isEmojiReceivedMessage,
  isPingReceivedMessage,
  isSectionPresenceStateMessage,
  isSectionPresenceUpdateMessage,
  isSectionCursorPositionMessage,
  isBudgetStateMessage,
  isBudgetUpdateMessage,
  isBudgetRefreshMessage,
  isButtonHoverUpdateMessage,
  isButtonHoverStateMessage,
  isUnlockCitySuccessMessage,
  isUnlockCityFailedMessage,
  isCommercialActionFailedMessage,
  isUnlockedCitiesStateMessage,
  isWorldSaveStartMessage,
  isWorldSaveEndMessage,
  isWorldSavePrepMessage,
  isManifestTickStartMessage,
  isManifestTickEndMessage,
  isPlayerDisconnectedMessage,
  isRequestHelpSuccessMessage,
  isRequestHelpFailedMessage,
  isCancelHelpSuccessMessage,
  isHelpRequestResolvedMessage,
  isRouteShareReportMessage,
  isRouteShareFailedMessage,
  isPhaseChangeMessage,
  isSessionScriptStepMessage,
  isVoiceChatControlMessage,
  isGlobalAudioMuteMessage,
  isTeamActivityMessage,
  isAchievementAwardedMessage,
  isCommercialStateMessage,
  isInfluenceStateMessage,
} from '../constants/op-codes';

// Import domain handlers
import { handlePlayerJoined, handlePlayersList, handlePlayerDisconnected, handlePlayerLeft, handleBudgetState, handleBudgetUpdate, handleBudgetRefresh } from './players';
import { handleGlobalAudioMute, handleLiveKitToken, handleVoiceChatControl } from './voice-chat';
import { handlePhaseChange } from './match-phase';
import { handleSessionScriptStep } from './match-session-script';
import { handleTeamActivity } from './team-activity';
import { handleAchievementAwarded } from './achievements';
import { handlePrivateChatStart, handlePrivateChatEnd } from './private-voice-chat';
import { handleRequestHelpSuccess, handleRequestHelpFailed, handleCancelHelpSuccess, handleHelpRequestResolved } from './help-request';
import { handleAirportsState } from './airports';
import { handleTeamUpdate, handleTeamsList } from './teams';
import {
  handleFleetsState,
  handleAvailablePlanesState,
  handleAddFleetSlotSuccess,
  handleRemoveFleetSlotSuccess,
  handleUpdateFleetSlotSuccess,
  handleFleetOperationFailed,
  handlePurchaseAirplanesSuccess,
  handlePurchaseAirplanesFailed,
  handleSellAirplanesSuccess,
  handleSellAirplanesFailed,
  useFleetsStore,
} from './fleets';
import { fleetSlotChange, getFleetSlotLabel } from './fleets';
import {
  handleSegmentsState,
  handleAddSegmentSuccess,
  handleUpdateFlightFrequencySuccess,
  handleRemoveFlightSuccess,
  handleSegmentOperationFailed,
  handleTeamSegmentsState,
  handleTeamSegmentsUpdate,
  handleQueryMaxFlightsResult,
  useSegmentsStore,
} from './segments';
import {
  handleUpdateItineraryPriceSuccess,
  handleUpdateItineraryPriceFailed,
  handleUpdateRoutePricingPolicySuccess,
  handleUpdateRoutePricingPolicyFailed,
  handleResetItineraryPriceToAutoSuccess,
  handleResetItineraryPriceToAutoFailed,
  handleAirportFlightsState,
  handleAirportFlightUpdate,
  handleAirportFlightRemoved,
} from './itineraries';
import { handleFinancialReport, handleTimerSync, handleTickProcessingState } from './financials';
import { setAirplaneModels } from '../data/airplanes';
import { handleAnnualReport, handleCFOStatements, handleQuarterlyReport, handleTTMMetrics } from './financials-quarterly';
import { handleStrengthReport } from './strength';
import { useAirportsStore } from './airports';
import {
  handleDevSaveStressLocalSuccess,
  handleDevSaveStressLocalFailed,
} from './local-saves';
import {
  handleAdminAddResourcesSuccess,
  handleAdminAddResourcesFailed,
  handleAdminJumpToNextTickSuccess,
  handleAdminJumpToNextTickFailed,
  handleAdminSetInnovationSpendLockedSuccess,
  handleAdminSetInnovationSpendLockedFailed,
  handleAdminForceEndMatchSuccess,
  handleAdminForceEndMatchFailed,
} from './admin';
import {
  handleDebtQuoteSuccess,
  handleDebtOrderSuccess,
  handleEquityQuoteSuccess,
  handleEquityOrderSuccess,
  handleCapitalError,
  handleCapitalStateSync,
} from './capital';
import {
  handleUnlockInnovationSuccess,
  handleUnlockInnovationFailed,
} from './innovation';
import {
  handleMissionsState,
  handleMissionProgress,
  handleMissionCompleted,
} from './missions';
import {
  handlePolicyStatesSync,
  handleTogglePolicySuccess,
  handleTogglePolicyFailed,
} from './policy-toggles';
import { handleEmojiReceived } from './emoji';
import { handlePingReceived } from './globe-ping';
import {
  handleRouteShareReport,
  handleRouteShareFailed,
} from './route-share';
import {
  handleSectionPresenceState,
  handleSectionPresenceUpdate,
} from './section-presence';
import { handleSectionCursorPosition } from './section-cursor';
import { handleButtonHoverUpdate, handleButtonHoverState } from './cosign';
import {
  handleUnlockedCitiesState,
  handleUnlockCitySuccess,
  handleUnlockCityFailed,
} from './city-access';
import {
  handleWorldSavePrep,
  handleWorldSaveStart,
  handleWorldSaveEnd,
  handleManifestTickStart,
  handleManifestTickEnd,
} from './server-operations';
import { addActivityLogEntry } from './activity-log';

/**
 * Whether a message is in the shape its handler reads (`guard`). Every case
 * below reads its message through here, so a message the router acts on
 * passed its guard first. An answer to the command on its way opens the
 * command gate here, and only once it is in shape, just before its handler
 * ends the sender's wait; an answer out of shape, whose handler will not run,
 * ends that command as a lost answer does — the gate opens and the sender's
 * wait ends — rather than leave either waiting (command-gate.ts).
 */
function inShape<T>(opCode: number, data: unknown, guard: (data: unknown) => data is T): data is T {
  if (!guard(data)) {
    abandonUnreadableResponse(opCode, data);
    return false;
  }
  acknowledgeGatedResponse(opCode, data);
  return true;
}

/** The report messages a reports history may carry: the ones a join's history holds. */
const REPORTS_HISTORY_OP_CODES = new Set<number>([
  OpCode.OpCodeQuarterlyReport,
  OpCode.OpCodeAnnualReport,
  OpCode.OpCodeStrengthReport,
  OpCode.OpCodeCFOStatements,
  OpCode.OpCodeTTMMetrics,
]);

/**
 * The reports' history a join is sent, batched (reports.go,
 * SendReportsHistory): each report it carries is routed as if it had come
 * under its own op code, in order, so its handler and its guard are the ones a
 * live close goes through. A message of any other kind inside it is not routed.
 */
function routeReportsHistory(data: ReportsHistoryMessage): void {
  for (const message of data.messages) {
    if (REPORTS_HISTORY_OP_CODES.has(message.op_code)) {
      routeMessage(message.op_code, message.payload);
    } else {
      console.warn('[DomainMessageRouter] A reports history carried op code', message.op_code, 'which it never holds');
    }
  }
}

/**
 * Routes a decoded match data message to the appropriate domain handler, at
 * once: the service routes messages in the order they arrive
 * (nakama-service.ts), so each is applied before the next is routed — a
 * rejoin's snapshot has landed when the player's own join is routed.
 * @param opCode - Operation code from the server
 * @param data - Decoded message data
 */
function routeMessage(opCode: number, data: unknown): void {
  switch (opCode) {
    // ===== PLAYER MESSAGES =====
    case OpCode.OpCodeInfluenceState:
      if (inShape(opCode, data, isInfluenceStateMessage)) {
        handleInfluenceState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid InfluenceState message format:', data);
      }
      break;
    case OpCode.OpCodeCommercialState:
      if (inShape(opCode, data, isCommercialStateMessage)) {
        handleCommercialState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CommercialState message format:', data);
      }
      break;
    case OpCode.OpCodeCommercialActionFailed:
      if (inShape(opCode, data, isCommercialActionFailedMessage)) {
        handleCommercialFailure(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CommercialActionFailed message format:', data);
      }
      break;
    case OpCode.OpCodePlayerJoined:
      if (inShape(opCode, data, isPlayerJoinedMessage)) {
        handlePlayerJoined(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PlayerJoined message format:', data);
      }
      break;

    case OpCode.OpCodePlayersList:
      if (inShape(opCode, data, isPlayersListMessage)) {
        handlePlayersList(data);
        // The teams, when the list carries them, are the teams domain's.
        if (data.teams) handleTeamsList(data.teams);
      } else {
        console.warn('[DomainMessageRouter] Invalid PlayersList message format:', data);
      }
      break;

    case OpCode.OpCodePlayerDisconnected:
      if (inShape(opCode, data, isPlayerDisconnectedMessage)) {
        handlePlayerDisconnected(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PlayerDisconnected message format:', data);
      }
      break;

    case OpCode.OpCodePlayerLeft:
      if (inShape(opCode, data, isPlayerLeftMessage)) {
        handlePlayerLeft(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PlayerLeft message format:', data);
      }
      break;

    case OpCode.OpCodePhaseChange:
      // Session lifecycle phase changed.
      if (inShape(opCode, data, isPhaseChangeMessage)) {
        handlePhaseChange(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PhaseChange message format:', data);
      }
      break;

    case OpCode.OpCodeSessionScriptStep:
      // Server-controlled session script step changed — update session script state.
      if (inShape(opCode, data, isSessionScriptStepMessage)) {
        handleSessionScriptStep(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid session_script_step message format:', data);
      }
      break;

    case OpCode.OpCodeVoiceChatControl:
      // Server enabling/disabling voice chat for all players (script feature).
      if (inShape(opCode, data, isVoiceChatControlMessage)) {
        handleVoiceChatControl(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid voice_chat_control message format:', data);
      }
      break;

    case OpCode.OpCodeGlobalAudioMute:
      // Session-level audio mute — silence all remote audio output (mics unaffected).
      if (inShape(opCode, data, isGlobalAudioMuteMessage)) {
        handleGlobalAudioMute(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid global_audio_mute message format:', data);
      }
      break;

    case OpCode.OpCodeSessionEnded:
      // The session is over — its end lobby ran out, or its world was reset
      // or deleted mid-play — and the server ends the match next.
      if (inShape(opCode, data, isSessionEndedMessage)) {
        console.info('[DomainMessageRouter] Session ended:', data);
        handleSessionEnded(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SessionEnded message format:', data);
      }
      break;

    case OpCode.OpCodePlayerBanned:
      // A moderator removed the player: the notice opens, and the server
      // closes the socket next.
      if (inShape(opCode, data, isPlayerBannedMessage)) {
        console.warn('[DomainMessageRouter] Player banned:', data);
        handlePlayerBanned(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PlayerBanned message format:', data);
      }
      break;

    // ===== VOICE CHAT MESSAGES =====
    case OpCode.OpCodeLiveKitToken:
      if (inShape(opCode, data, isLiveKitTokenMessage)) {
        handleLiveKitToken(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid LiveKitToken message format:', data);
      }
      break;

    case OpCode.OpCodePrivateChatStart:
      if (inShape(opCode, data, isPrivateChatStartMessage)) {
        handlePrivateChatStart(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PrivateChatStart message format:', data);
      }
      break;

    case OpCode.OpCodePrivateChatEnd:
      if (inShape(opCode, data, isPrivateChatEndMessage)) {
        handlePrivateChatEnd(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PrivateChatEnd message format:', data);
      }
      break;

    // ===== HELP REQUEST MESSAGES =====
    case OpCode.OpCodeRequestHelpSuccess:
      if (inShape(opCode, data, isRequestHelpSuccessMessage)) {
        handleRequestHelpSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid RequestHelpSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeRequestHelpFailed:
      if (inShape(opCode, data, isRequestHelpFailedMessage)) {
        handleRequestHelpFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid RequestHelpFailed message format:', data);
      }
      break;

    case OpCode.OpCodeCancelHelpSuccess:
      if (inShape(opCode, data, isCancelHelpSuccessMessage)) {
        handleCancelHelpSuccess();
      } else {
        console.warn('[DomainMessageRouter] Invalid CancelHelpSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeHelpRequestResolved:
      if (inShape(opCode, data, isHelpRequestResolvedMessage)) {
        handleHelpRequestResolved();
      } else {
        console.warn('[DomainMessageRouter] Invalid HelpRequestResolved message format:', data);
      }
      break;

    // ===== AIRPORT MESSAGES =====
    case OpCode.OpCodeAirportsState:
      if (inShape(opCode, data, isAirportsStateMessage)) {
        handleAirportsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AirportsState message format:', data);
      }
      break;

    // ===== FLEET MESSAGES =====
    case OpCode.OpCodeFleetsState:
      if (inShape(opCode, data, isFleetsStateMessage)) {
        handleFleetsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid FleetsState message format:', data);
      }
      break;

    case OpCode.OpCodeAvailablePlanesState:
      if (inShape(opCode, data, isAvailablePlanesStateMessage)) {
        handleAvailablePlanesState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AvailablePlanesState message format:', data);
      }
      break;

    case OpCode.OpCodeAirplaneModelsState:
      if (inShape(opCode, data, isAirplaneModelsStateMessage)) {
        console.log('[DomainMessageRouter] Received airplane models from server');
        setAirplaneModels(data.airplane_models);
      } else {
        console.warn('[DomainMessageRouter] Invalid AirplaneModelsState message format:', data);
      }
      break;

    case OpCode.OpCodeAddFleetSlotSuccess:
      if (inShape(opCode, data, isAddFleetSlotSuccessMessage)) {
        console.log('[DomainMessageRouter] Fleet slot added successfully');
        
        // Get old fleet state to detect which model was added
        const oldFleet = useFleetsStore.getState().fleets[data.cohort_id];
        
        // IMPORTANT: Detect changes BEFORE updating the store!
        let addedModel: string | null = null;
        if (data.fleet && data.cohort_id) {
          addedModel = fleetSlotChange(oldFleet, data.fleet, 'added');
        }
        
        // Now update the store
        handleAddFleetSlotSuccess(data);
        
        // Add activity log entry if we detected a change
        if (addedModel) {
          addActivityLogEntry(
            'fleet_slot_added',
            { userId: data.sender_user_id, cohortId: data.cohort_id },
            {
              model: addedModel,
            }
          );
        }
      } else {
        console.warn('[DomainMessageRouter] Invalid AddFleetSlotSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAddFleetSlotFailed:
      if (inShape(opCode, data, isAddFleetSlotFailedMessage)) {
        handleFleetOperationFailed(data, 'Add fleet slot');
      } else {
        console.warn('[DomainMessageRouter] Invalid AddFleetSlotFailed message format:', data);
      }
      break;

    case OpCode.OpCodeRemoveFleetSlotSuccess:
      if (inShape(opCode, data, isRemoveFleetSlotSuccessMessage)) {
        console.log('[DomainMessageRouter] Fleet slot removed successfully');
        
        // Get old fleet state to detect which model was removed
        const oldFleetForRemove = useFleetsStore.getState().fleets[data.cohort_id];
        
        // IMPORTANT: Detect changes BEFORE updating the store!
        let removedModel: string | null = null;
        if (data.fleet && data.cohort_id) {
          removedModel = fleetSlotChange(oldFleetForRemove, data.fleet, 'removed');
        }
        
        // Now update the store
        handleRemoveFleetSlotSuccess(data);
        
        // Add activity log entry if we detected a change
        if (removedModel) {
          addActivityLogEntry(
            'fleet_slot_removed',
            { userId: data.sender_user_id, cohortId: data.cohort_id },
            {
              model: removedModel,
            }
          );
        }
      } else {
        console.warn('[DomainMessageRouter] Invalid RemoveFleetSlotSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeRemoveFleetSlotFailed:
      if (inShape(opCode, data, isRemoveFleetSlotFailedMessage)) {
        handleFleetOperationFailed(data, 'Remove fleet slot');
      } else {
        console.warn('[DomainMessageRouter] Invalid RemoveFleetSlotFailed message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateFleetSlotSuccess:
      if (inShape(opCode, data, isUpdateFleetSlotSuccessMessage)) {
        console.log('[DomainMessageRouter] Fleet slot updated successfully');
        
        // Get old fleet state to detect which model was updated
        const oldFleetForUpdate = useFleetsStore.getState().fleets[data.cohort_id];
        
        // IMPORTANT: Detect changes BEFORE updating the store!
        let updatedModel: string | null = null;
        if (data.fleet && data.cohort_id) {
          updatedModel = fleetSlotChange(oldFleetForUpdate, data.fleet, 'updated');
        }
        
        // Now update the store
        handleUpdateFleetSlotSuccess(data);
        
        // Add activity log entry if we detected a change
        if (updatedModel) {
          addActivityLogEntry(
            'fleet_slot_updated',
            { userId: data.sender_user_id, cohortId: data.cohort_id },
            {
              model: updatedModel,
            }
          );
        }
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateFleetSlotSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateFleetSlotFailed:
      if (inShape(opCode, data, isUpdateFleetSlotFailedMessage)) {
        handleFleetOperationFailed(data, 'Update fleet slot');
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateFleetSlotFailed message format:', data);
      }
      break;

    case OpCode.OpCodePurchaseAirplanesSuccess:
      if (inShape(opCode, data, isPurchaseAirplanesSuccessMessage)) {
        handlePurchaseAirplanesSuccess(data);

        // Add activity log entry for airplane purchase
        addActivityLogEntry(
          'airplane_purchased',
          { userId: data.sender_user_id, cohortId: data.cohort_id },
          {
            model: getFleetSlotLabel(data.fleet?.slots?.[data.slot_index], data.slot_index),
            quantity: data.quantity,
          }
        );
      } else {
        console.warn('[DomainMessageRouter] Invalid PurchaseAirplanesSuccess message format:', data);
      }
      break;

    case OpCode.OpCodePurchaseAirplanesFailed:
      if (inShape(opCode, data, isPurchaseAirplanesFailedMessage)) {
        handlePurchaseAirplanesFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PurchaseAirplanesFailed message format:', data);
      }
      break;
    case OpCode.OpCodeSellAirplanesSuccess:
      if (inShape(opCode, data, isSellAirplanesSuccessMessage)) {
        handleSellAirplanesSuccess(data);

        // Add activity log entry for airplane sale
        addActivityLogEntry(
          'airplane_sold',
          { userId: data.sender_user_id, cohortId: data.cohort_id },
          {
            model: getFleetSlotLabel(data.fleet?.slots?.[data.slot_index], data.slot_index),
            quantity: data.quantity,
          }
        );
      } else {
        console.warn('[DomainMessageRouter] Invalid SellAirplanesSuccess message format:', data);
      }
      break;
    case OpCode.OpCodeSellAirplanesFailed:
      if (inShape(opCode, data, isSellAirplanesFailedMessage)) {
        handleSellAirplanesFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SellAirplanesFailed message format:', data);
      }
      break;

    // ===== QUERY MAX FLIGHTS =====
    case OpCode.OpCodeQueryMaxFlightsResult:
      if (inShape(opCode, data, isQueryMaxFlightsResultMessage)) {
        handleQueryMaxFlightsResult(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid QueryMaxFlightsResult message format:', data);
      }
      break;

    // ===== SEGMENT MESSAGES =====
    case OpCode.OpCodeSegmentsState:
      if (inShape(opCode, data, isSegmentsStateMessage)) {
        handleSegmentsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SegmentsState message format:', data);
      }
      break;

    case OpCode.OpCodeAddSegmentSuccess:
      if (inShape(opCode, data, isAddSegmentSuccessMessage)) {
        console.log('[DomainMessageRouter] Segment added successfully');
        handleAddSegmentSuccess(data);
        
        // Add activity log entry for the first flight added with this segment
        const originAirport = useAirportsStore.getState().airports[data.segment.airport_id_origin];
        const destAirport = useAirportsStore.getState().airports[data.segment.airport_id_dest];
        addActivityLogEntry(
          'flight_added',
          { userId: data.sender_user_id, cohortId: data.cohort_id },
          {
            origin: originAirport?.code,
            dest: destAirport?.code,
          }
        );
      } else {
        console.warn('[DomainMessageRouter] Invalid AddSegmentSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAddSegmentFailed:
      if (inShape(opCode, data, isAddSegmentFailedMessage)) {
        handleSegmentOperationFailed(data, 'Add segment');
      } else {
        console.warn('[DomainMessageRouter] Invalid AddSegmentFailed message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateFlightFrequencySuccess:
      if (inShape(opCode, data, isUpdateFlightFrequencySuccessMessage)) {
        console.log('[DomainMessageRouter] Flight frequency updated successfully');
        handleUpdateFlightFrequencySuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateFlightFrequencySuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateFlightFrequencyFailed:
      if (inShape(opCode, data, isUpdateFlightFrequencyFailedMessage)) {
        handleSegmentOperationFailed(data, 'Update flight frequency');
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateFlightFrequencyFailed message format:', data);
      }
      break;

    case OpCode.OpCodeRemoveFlightSuccess:
      if (inShape(opCode, data, isRemoveFlightSuccessMessage)) {
        console.log('[DomainMessageRouter] Flight removed successfully');

        // Get segment info BEFORE removal for activity log
        const removedFlightSegment = useSegmentsStore.getState().getSegment(data.segment_id);
        const flightAirports = useAirportsStore.getState().airports;
        const flightOriginCode = removedFlightSegment ? flightAirports[removedFlightSegment.airport_id_origin]?.code : undefined;
        const flightDestCode = removedFlightSegment ? flightAirports[removedFlightSegment.airport_id_dest]?.code : undefined;

        handleRemoveFlightSuccess(data);

        // Always add activity log entry for flight cancellation
        addActivityLogEntry(
          'flight_cancelled',
          { userId: data.sender_user_id, cohortId: data.cohort_id },
          {
            origin: flightOriginCode,
            dest: flightDestCode,
          }
        );
      } else {
        console.warn('[DomainMessageRouter] Invalid RemoveFlightSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeRemoveFlightFailed:
      if (inShape(opCode, data, isRemoveFlightFailedMessage)) {
        handleSegmentOperationFailed(data, 'Remove flight');
      } else {
        console.warn('[DomainMessageRouter] Invalid RemoveFlightFailed message format:', data);
      }
      break;

    // ===== TEAM SEGMENTS SUBSCRIPTION MESSAGES =====
    case OpCode.OpCodeTeamSegmentsState:
      if (inShape(opCode, data, isTeamSegmentsStateMessage)) {
        handleTeamSegmentsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TeamSegmentsState message format:', data);
      }
      break;

    case OpCode.OpCodeTeamSegmentsUpdate:
      if (inShape(opCode, data, isTeamSegmentsUpdateMessage)) {
        handleTeamSegmentsUpdate(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TeamSegmentsUpdate message format:', data);
      }
      break;

    // ===== ITINERARY MESSAGES =====
    case OpCode.OpCodeUpdateItineraryPriceSuccess:
      if (inShape(opCode, data, isUpdateItineraryPriceSuccessMessage)) {
        handleUpdateItineraryPriceSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateItineraryPriceSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateItineraryPriceFailed:
      if (inShape(opCode, data, isUpdateItineraryPriceFailedMessage)) {
        handleUpdateItineraryPriceFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateItineraryPriceFailed message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateRoutePricingPolicySuccess:
      if (inShape(opCode, data, isUpdateRoutePricingPolicySuccessMessage)) {
        handleUpdateRoutePricingPolicySuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateRoutePricingPolicySuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUpdateRoutePricingPolicyFailed:
      if (inShape(opCode, data, isUpdateRoutePricingPolicyFailedMessage)) {
        handleUpdateRoutePricingPolicyFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UpdateRoutePricingPolicyFailed message format:', data);
      }
      break;

    case OpCode.OpCodeResetItineraryPriceToAutoSuccess:
      if (inShape(opCode, data, isResetItineraryPriceToAutoSuccessMessage)) {
        handleResetItineraryPriceToAutoSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ResetItineraryPriceToAutoSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeResetItineraryPriceToAutoFailed:
      if (inShape(opCode, data, isResetItineraryPriceToAutoFailedMessage)) {
        handleResetItineraryPriceToAutoFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ResetItineraryPriceToAutoFailed message format:', data);
      }
      break;

    // ===== AIRPORT FLIGHTS MESSAGES =====
    case OpCode.OpCodeAirportFlightsState:
      if (inShape(opCode, data, isAirportFlightsStateMessage)) {
        handleAirportFlightsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AirportFlightsState message format:', data);
      }
      break;

    case OpCode.OpCodeAirportFlightUpdate:
      if (inShape(opCode, data, isAirportFlightUpdateMessage)) {
        handleAirportFlightUpdate(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AirportFlightUpdate message format:', data);
      }
      break;

    case OpCode.OpCodeAirportFlightRemoved:
      if (inShape(opCode, data, isAirportFlightRemovedMessage)) {
        handleAirportFlightRemoved(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AirportFlightRemoved message format:', data);
      }
      break;

    // ===== FINANCIAL MESSAGES =====
    case OpCode.OpCodeFinancialReport:
      if (inShape(opCode, data, isFinancialReportMessage)) {
        handleFinancialReport(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid FinancialReport message format:', data);
      }
      break;

    case OpCode.OpCodeTimerSync:
      if (inShape(opCode, data, isTimerSyncMessage)) {
        handleTimerSync(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TimerSync message format:', data);
      }
      break;

    case OpCode.OpCodeTickProcessingState:
      if (inShape(opCode, data, isTickProcessingStateMessage)) {
        handleTickProcessingState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TickProcessingState message format:', data);
      }
      break;

    case OpCode.OpCodeQuarterlyReport:
      if (inShape(opCode, data, isQuarterlyReportMessage)) {
        handleQuarterlyReport(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid QuarterlyReport message format:', data);
      }
      break;

    case OpCode.OpCodeAnnualReport:
      if (inShape(opCode, data, isAnnualReportMessage)) {
        handleAnnualReport(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AnnualReport message format:', data);
      }
      break;

    case OpCode.OpCodeCFOStatements:
      if (inShape(opCode, data, isCFOStatementsMessage)) {
        handleCFOStatements(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CFOStatements message format:', data);
      }
      break;

    case OpCode.OpCodeTTMMetrics:
      if (inShape(opCode, data, isTTMMetricsMessage)) {
        handleTTMMetrics(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TTMMetrics message format:', data);
      }
      break;

    case OpCode.OpCodeStrengthReport:
      if (inShape(opCode, data, isStrengthReportMessage)) {
        handleStrengthReport(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid StrengthReport message format:', data);
      }
      break;

    case OpCode.OpCodeReportsHistory:
      if (inShape(opCode, data, isReportsHistoryMessage)) {
        routeReportsHistory(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ReportsHistory message format:', data);
      }
      break;

    // ===== CAPITAL MESSAGES =====
    case OpCode.OpCodeCapitalDebtQuoteSuccess:
      if (inShape(opCode, data, isCapitalDebtQuoteSuccessMessage)) {
        handleDebtQuoteSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalDebtQuoteSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalDebtQuoteFailed:
      if (inShape(opCode, data, isCapitalDebtQuoteFailedMessage)) {
        handleCapitalError(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalDebtQuoteFailed message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalDebtOrderSuccess:
      if (inShape(opCode, data, isCapitalDebtOrderSuccessMessage)) {
        handleDebtOrderSuccess(data);
        if (data.team?.cohort_id_backend) {
          handleTeamUpdate(data.team.cohort_id_backend, data.team);
        }
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalDebtOrderSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalDebtOrderFailed:
      if (inShape(opCode, data, isCapitalDebtOrderFailedMessage)) {
        handleCapitalError(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalDebtOrderFailed message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalEquityQuoteSuccess:
      if (inShape(opCode, data, isCapitalEquityQuoteSuccessMessage)) {
        handleEquityQuoteSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalEquityQuoteSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalEquityQuoteFailed:
      if (inShape(opCode, data, isCapitalEquityQuoteFailedMessage)) {
        handleCapitalError(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalEquityQuoteFailed message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalEquityOrderSuccess:
      if (inShape(opCode, data, isCapitalEquityOrderSuccessMessage)) {
        handleEquityOrderSuccess(data);
        if (data.team?.cohort_id_backend) {
          handleTeamUpdate(data.team.cohort_id_backend, data.team);
        }
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalEquityOrderSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalEquityOrderFailed:
      if (inShape(opCode, data, isCapitalEquityOrderFailedMessage)) {
        handleCapitalError(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalEquityOrderFailed message format:', data);
      }
      break;

    case OpCode.OpCodeCapitalState:
      if (inShape(opCode, data, isCapitalStateMessage)) {
        handleCapitalStateSync(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid CapitalState message format:', data);
      }
      break;

    // ===== INNOVATION MESSAGES =====
    case OpCode.OpCodeUnlockInnovationSuccess:
      if (inShape(opCode, data, isUnlockInnovationSuccessMessage)) {
        handleUnlockInnovationSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UnlockInnovationSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUnlockInnovationFailed:
      if (inShape(opCode, data, isUnlockInnovationFailedMessage)) {
        handleUnlockInnovationFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UnlockInnovationFailed message format:', data);
      }
      break;

    // ===== MISSIONS MESSAGES =====
    case OpCode.OpCodeMissionsState:
      if (inShape(opCode, data, isMissionsStateMessage)) {
        handleMissionsState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid MissionsState message format:', data);
      }
      break;

    case OpCode.OpCodeMissionProgress:
      if (inShape(opCode, data, isMissionProgressMessage)) {
        handleMissionProgress(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid MissionProgress message format:', data);
      }
      break;

    case OpCode.OpCodeMissionCompleted:
      if (inShape(opCode, data, isMissionCompletedMessage)) {
        handleMissionCompleted(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid MissionCompleted message format:', data);
      }
      break;

    // ===== DEVTOOLS MESSAGES =====
    case OpCode.OpCodeDevSaveStressLocalSuccess:
      if (inShape(opCode, data, isDevSaveStressLocalSuccessMessage)) {
        handleDevSaveStressLocalSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid DevSaveStressLocalSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeDevSaveStressLocalFailed:
      if (inShape(opCode, data, isDevSaveStressLocalFailedMessage)) {
        handleDevSaveStressLocalFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid DevSaveStressLocalFailed message format:', data);
      }
      break;

    // ===== ADMIN CHEAT MESSAGES =====
    case OpCode.OpCodeAdminAddResourcesSuccess:
      if (inShape(opCode, data, isAdminAddResourcesSuccessMessage)) {
        handleAdminAddResourcesSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminAddResourcesSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAdminAddResourcesFailed:
      if (inShape(opCode, data, isAdminAddResourcesFailedMessage)) {
        handleAdminAddResourcesFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminAddResourcesFailed message format:', data);
      }
      break;

    case OpCode.OpCodeAdminJumpToNextTickSuccess:
      if (inShape(opCode, data, isAdminJumpToNextTickSuccessMessage)) {
        handleAdminJumpToNextTickSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminJumpToNextTickSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAdminJumpToNextTickFailed:
      if (inShape(opCode, data, isAdminJumpToNextTickFailedMessage)) {
        handleAdminJumpToNextTickFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminJumpToNextTickFailed message format:', data);
      }
      break;

    case OpCode.OpCodeAdminSetInnovationSpendLockedSuccess:
      if (inShape(opCode, data, isAdminSetInnovationSpendLockedSuccessMessage)) {
        handleAdminSetInnovationSpendLockedSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminSetInnovationSpendLockedSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAdminSetInnovationSpendLockedFailed:
      if (inShape(opCode, data, isAdminSetInnovationSpendLockedFailedMessage)) {
        handleAdminSetInnovationSpendLockedFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminSetInnovationSpendLockedFailed message format:', data);
      }
      break;

    case OpCode.OpCodeAdminForceEndMatchSuccess:
      if (inShape(opCode, data, isAdminForceEndMatchSuccessMessage)) {
        handleAdminForceEndMatchSuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminForceEndMatchSuccess message format:', data);
      }
      break;

    case OpCode.OpCodeAdminForceEndMatchFailed:
      if (inShape(opCode, data, isAdminForceEndMatchFailedMessage)) {
        handleAdminForceEndMatchFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AdminForceEndMatchFailed message format:', data);
      }
      break;

    // ===== POLICY TOGGLE MESSAGES =====
    case OpCode.OpCodePolicyStatesSync:
      if (inShape(opCode, data, isPolicyStatesSyncMessage)) {
        handlePolicyStatesSync(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PolicyStatesSync message format:', data);
      }
      break;

    case OpCode.OpCodeTogglePolicySuccess:
      if (inShape(opCode, data, isTogglePolicySuccessMessage)) {
        handleTogglePolicySuccess(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TogglePolicySuccess message format:', data);
      }
      break;

    case OpCode.OpCodeTogglePolicyFailed:
      if (inShape(opCode, data, isTogglePolicyFailedMessage)) {
        handleTogglePolicyFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TogglePolicyFailed message format:', data);
      }
      break;

    // ===== EMOJI =====
    case OpCode.OpCodeEmojiReceived:
      if (inShape(opCode, data, isEmojiReceivedMessage)) {
        handleEmojiReceived(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid EmojiReceived message format:', data);
      }
      break;

    // ===== PING =====
    case OpCode.OpCodePingReceived:
      if (inShape(opCode, data, isPingReceivedMessage)) {
        handlePingReceived(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid PingReceived message format:', data);
      }
      break;

    // ===== ROUTE SHARE =====
    case OpCode.OpCodeRouteShareReport:
      if (inShape(opCode, data, isRouteShareReportMessage)) {
        handleRouteShareReport(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid RouteShareReport message format:', data);
      }
      break;

    case OpCode.OpCodeRouteShareFailed:
      if (inShape(opCode, data, isRouteShareFailedMessage)) {
        handleRouteShareFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid RouteShareFailed message format:', data);
      }
      break;

    // ===== SECTION PRESENCE =====
    case OpCode.OpCodeSectionPresenceState:
      if (inShape(opCode, data, isSectionPresenceStateMessage)) {
        handleSectionPresenceState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SectionPresenceState message format:', data);
      }
      break;

    case OpCode.OpCodeSectionPresenceUpdate:
      if (inShape(opCode, data, isSectionPresenceUpdateMessage)) {
        handleSectionPresenceUpdate(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SectionPresenceUpdate message format:', data);
      }
      break;

    // ===== SECTION CURSOR =====
    case OpCode.OpCodeSectionCursorPosition:
      if (inShape(opCode, data, isSectionCursorPositionMessage)) {
        handleSectionCursorPosition(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid SectionCursorPosition message format:', data);
      }
      break;

    // ===== CO-SIGN =====
    case OpCode.OpCodeButtonHover:
      if (inShape(opCode, data, isButtonHoverUpdateMessage)) {
        handleButtonHoverUpdate(data);
      } else if (inShape(opCode, data, isButtonHoverStateMessage)) {
        handleButtonHoverState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ButtonHover message format:', data);
      }
      break;

    // ===== BUDGET =====
    case OpCode.OpCodeBudgetState:
      if (inShape(opCode, data, isBudgetStateMessage)) {
        handleBudgetState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid BudgetState message format:', data);
      }
      break;

    case OpCode.OpCodeBudgetUpdate:
      if (inShape(opCode, data, isBudgetUpdateMessage)) {
        handleBudgetUpdate(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid BudgetUpdate message format:', data);
      }
      break;

    case OpCode.OpCodeBudgetRefresh:
      if (inShape(opCode, data, isBudgetRefreshMessage)) {
        handleBudgetRefresh(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid BudgetRefresh message format:', data);
      }
      break;

    // ===== CITY ACCESS =====
    case OpCode.OpCodeUnlockCitySuccess:
      if (inShape(opCode, data, isUnlockCitySuccessMessage)) {
        console.log('[DomainMessageRouter] City unlocked successfully');
        handleUnlockCitySuccess(data);

        // Add activity log entry
        const unlockedAirportCode =
          data.airport?.code ?? useAirportsStore.getState().airports[data.city_id]?.code ?? '';
        addActivityLogEntry(
          'city_unlocked',
          { userId: data.sender_user_id, cohortId: data.cohort_id },
          {
            airport: unlockedAirportCode || undefined,
          }
        );
      } else {
        console.warn('[DomainMessageRouter] Invalid UnlockCitySuccess message format:', data);
      }
      break;

    case OpCode.OpCodeUnlockCityFailed:
      if (inShape(opCode, data, isUnlockCityFailedMessage)) {
        handleUnlockCityFailed(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UnlockCityFailed message format:', data);
      }
      break;

    case OpCode.OpCodeUnlockedCitiesState:
      if (inShape(opCode, data, isUnlockedCitiesStateMessage)) {
        handleUnlockedCitiesState(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid UnlockedCitiesState message format:', data);
      }
      break;

    case OpCode.OpCodeWorldSavePrep:
      if (inShape(opCode, data, isWorldSavePrepMessage)) {
        handleWorldSavePrep(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid WorldSavePrep message format:', data);
      }
      break;

    case OpCode.OpCodeWorldSaveStart:
      if (inShape(opCode, data, isWorldSaveStartMessage)) {
        handleWorldSaveStart(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid WorldSaveStart message format:', data);
      }
      break;

    case OpCode.OpCodeWorldSaveEnd:
      if (inShape(opCode, data, isWorldSaveEndMessage)) {
        handleWorldSaveEnd(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid WorldSaveEnd message format:', data);
      }
      break;

    case OpCode.OpCodeManifestTickStart:
      if (inShape(opCode, data, isManifestTickStartMessage)) {
        handleManifestTickStart(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ManifestTickStart message format:', data);
      }
      break;

    case OpCode.OpCodeManifestTickEnd:
      if (inShape(opCode, data, isManifestTickEndMessage)) {
        handleManifestTickEnd(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid ManifestTickEnd message format:', data);
      }
      break;

    // ===== TEAM ACTIVITY =====
    case OpCode.OpCodeTeamActivity:
      if (inShape(opCode, data, isTeamActivityMessage)) {
        handleTeamActivity(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid TeamActivity message format:', data);
      }
      break;

    // ===== ACHIEVEMENTS =====
    case OpCode.OpCodeAchievementAwarded:
      if (inShape(opCode, data, isAchievementAwardedMessage)) {
        handleAchievementAwarded(data);
      } else {
        console.warn('[DomainMessageRouter] Invalid AchievementAwarded message format:', data);
      }
      break;

    // ===== UNKNOWN =====
    default:
      console.warn('[DomainMessageRouter] ⚠️ Unknown op code:', opCode, data);
      break;
  }
}

// ==================== AUTO-REGISTRATION ====================
// Register the router with NakamaService when this module is loaded
// This is the dependency injection point - the router injects itself into the service

nakamaService.registerMessageRouter(routeMessage);
console.log('[DomainMessageRouter] ✅ Auto-registered with NakamaService');
